// src/pages/private/sinisters/SinisterDetailsPresentation.tsx

import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useAuthContext } from "../../../context";
import { useProfile } from "../../../context/ProfileContext";
import { ToolbarSinister } from "../../../shared/components/toolbarSinister/toolbarSinister";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { globalMaskDocument } from "../../../shared/utils";
import { getFollowUpServiceByContext } from "../../client/components/ModalFollowUpGeneric/FollowUpService";
import { GenericFollowUpTabsModalContent } from "../../client/components/ModalFollowUpGeneric/GenericFollowUpTabsModalContent";
import { SinisterInternalFollowUpService } from "../../../shared/services/api/sinister/SinisterInternalFollowUpService";
import { handlePermissionSendFollowUp } from "../helpers/sinisterHelpers";
import { useFinalizeSinisterStatus } from "../hooks/useFinalizeSinisterStatus";
import { useGetSinisterDetails } from "../hooks/useGetSinisterDetails";
import { useSinisterSummary } from "../hooks/useSinisterSummary";
import { useUpdateResponsibleSinister } from "../hooks/useUpdateResponsibleSinister";
import { useUpdateSinisterProperty } from "../hooks/useUpdateSinisterProperty";
import { useUpdateSinisterStatus } from "../hooks/useUpdateSinisterStatus";
import ModalCancelSinister from "./Modals/ModalCancelSinister";
import ModalUpdateProperty from "./Modals/ModalUpdateProperty";
import ModalUpdateResponsible from "./Modals/ModalUpdateResponsible";
import { ModalUpdateStatus } from "./Modals/ModalUpdateStatus";
import SinisterCardData from "./SinisterCardData/SinisterCardData";
import { useTotalLmi } from "../hooks/useTotalLMI";
import { useChecklistSinister } from "../hooks/useChecklistSinister";
import { ModalDocumentsSinister } from "./Modals/ModalDocumentsSinister";

export const SinisterDetailsPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const { loggedUser } = useAuthContext();
	const { isSuperAdmin, permissionLevel } = useProfile();
	const followUpServices = getFollowUpServiceByContext("sinister");
	const internalFollowUpServices = {
		getNotes: (sinisterId: string) => SinisterInternalFollowUpService.getSinisterInternalFollowupNotes(sinisterId),
		addNote: (sinisterId: string, message: string) => SinisterInternalFollowUpService.addSinisterInternalFollowupNote(sinisterId, message),
	};
	const { data: rows, isLoading, refetch: fetchSinisterDetails } = useGetSinisterDetails(id);
	const { responsibleName, totalBordero, rowSummary, refetch: refetchSinisterSummary } = useSinisterSummary(id);
	const { data: totalLmi } = useTotalLmi(id, [rowSummary]);
	const [statusSinister, setStatusSinister] = useState("");
	const [busyProperty, setBusyProperty] = useState("");
	const { checklist, checkStatus, showButtonSendSinister, isLoading: isChecklistLoading, fetchChecklist } = useChecklistSinister();

	const canOpenResponsibleModal = useMemo(() => {
		return (isSuperAdmin || (permissionLevel != null && permissionLevel < 5)) && statusSinister !== "sketch";
	}, [isSuperAdmin, permissionLevel, statusSinister]);

	const canUpdateStatus = useMemo(() => {
		if (isSuperAdmin) return true;

		const hasPermission = checkByProfileGuard(PermissionRolesEnum.SinisterOptions, PermissionActionsEnum.AlterStatus);
		const isEligibleStatus = statusSinister !== "sketch" && statusSinister !== "open";

		return hasPermission && isEligibleStatus;
	}, [isSuperAdmin, statusSinister]);

	const canUpdateProperty = useMemo(() => {
		return statusSinister && statusSinister !== "finalized" && statusSinister !== "canceled";
	}, [statusSinister]);

	const canOpenValues = useMemo(() => {
		const hasPermission = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.View);

		if (!hasPermission) return false;

		if (statusSinister === "sketch") {
			return checkStatus; // libera se checklist está ok
		}

		return statusSinister !== "canceled" && statusSinister !== "finalized";
	}, [statusSinister, checkStatus]);

	const canCancelSinister = useMemo(() => {
		if (permissionLevel === 10 || statusSinister === "finalized" || statusSinister === "canceled") return false;

		return isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterOptions, PermissionActionsEnum.Cancel);
	}, [statusSinister, isSuperAdmin, permissionLevel]);

	const canOpenDocuments = useMemo(() => {
		const statusPermitidos = [
			"open",
			"in_progress",
			"in_analysis",
			"debts_confession",
			"monthly_compensation",
			"pending",
			"sketch",
			"preventive",
			"judicial",
			"extrajudicial",
			"canceled",
			"finalized",
		];
		return statusPermitidos.includes(statusSinister);
	}, [statusSinister]);

	useEffect(() => {
		if (id && id !== "novo") {
			fetchSinisterDetails(); // ✅ carrega dados
		}
	}, [id]);

	useEffect(() => {
		if (rows) {
			setStatusSinister(rows.status ?? "");
			setBusyProperty(rows.busyProperty ?? "");
		}
	}, [rows]);

	useEffect(() => {
		if (id && busyProperty) {
			fetchChecklist(id, busyProperty);
		}
	}, [id, busyProperty]);

	const { update: updateProperty, isLoading: isUpdatingProperty } = useUpdateSinisterProperty(fetchSinisterDetails, () =>
		handleToggleModal("openUpdateProperty", false)
	);

	const { update: updateStatus, isLoading: isUpdatingStatus } = useUpdateSinisterStatus(fetchSinisterDetails, () =>
		handleToggleModal("openUpdateStatus", false)
	);

	const { finalize: finalizeStatus, isLoading: isFinalizingStatus } = useFinalizeSinisterStatus(fetchSinisterDetails, () =>
		handleToggleModal("openUpdateStatus", false)
	);

	const { handleUpdate: updateResponsible, isLoading: isUpdatingResponsible } = useUpdateResponsibleSinister({
		id,
		schema: yup.object().shape({
			responsibleId: yup.string().required("Responsável é obrigatório"),
			responsibleEmail: yup.string().trim().email("Informe um e-mail válido").notRequired(),
		}),
		onSuccess: () => {
			handleToggleModal("openResponsible", false);
			fetchSinisterDetails();
			refetchSinisterSummary();
		},
	});

	const [modalStates, setModalStates] = useState({
		openUpdateStatus: false,
		openUpdateProperty: false,
		openFollowUp: false,
		openResponsible: false,
		openCancel: false,
		openCheckList: false,
	});

	const handleToggleModal = useCallback((modal: keyof typeof modalStates, isOpen: boolean) => {
		setModalStates((prev) => ({ ...prev, [modal]: isOpen }));
	}, []);

	return (
		<LayoutBaseDePagina
			titulo="Sinistro"
			subtitulo={`Detalhes do Sinistro [ ${rows?.proposal?.name ?? "N/D"} - ${globalMaskDocument(rows?.proposal?.document ?? "")} ]`}
			barraDeFerramentas={
				<ToolbarSinister
					clickButtonBack={() => navigate("/sinistros")}
					showButtonDocument={canOpenDocuments}
					clickButtonDocument={() => {
						if (canOpenDocuments) {
							handleToggleModal("openCheckList", true);
						}
					}}
					showButtonCancel={canCancelSinister}
					clickButtonCancel={() => {
						if (canCancelSinister) handleToggleModal("openCancel", true);
					}}
					showButtonValid={false}
					showButtonConfirm={false}
					showButtonFinish={false}
					showButtonResponsible={(isSuperAdmin || (permissionLevel != null && permissionLevel < 5)) && rows?.status !== "sketch"}
					clickButtonResponsible={() => {
						if (canOpenResponsibleModal) handleToggleModal("openResponsible", true);
					}}
					showButtonValues={canOpenValues}
					clickButtonValues={() => {
						if (canOpenValues) navigate(`/sinistros/valores/${id}`);
					}}
					clickButtonFollowUp={() => handleToggleModal("openFollowUp", true)}
					showButtonBackLoading={isLoading}
					showButtonCancelLoading={isLoading}
					showButtonFinishLoading={isLoading}
					showButtonDocumentLoading={isLoading}
					showButtonValuesLoading={isLoading}
					showButtonValidLoading={isLoading}
					showButtonConfirmLoading={isLoading}
					showButtonResponsibleLoading={isLoading}
					showButtonFollowUpLoading={isLoading}
				/>
			}
		>
			<Grid container gap={1} sx={{ width: "100%", maxWidth: "1300px" }}>
				{rows && (
					<>
						<SinisterCardData
							row={rows}
							onUpdatePropertyClick={() => handleToggleModal("openUpdateProperty", true)}
							onUpdateStatusClick={() => handleToggleModal("openUpdateStatus", true)}
							responsibleName={responsibleName}
							totalBordero={totalBordero}
							sumLmi={
								totalLmi ?? {
									lmiTotal: "",
									saldo: "",
									valor: 0,
									valorAprovado: 0,
								}
							}
							showUpdateStatus={canUpdateStatus}
							showUpdateProperty={!!canUpdateProperty}
						/>

						<ModalUpdateProperty
							open={modalStates.openUpdateProperty}
							isLoading={isLoading}
							isUploading={isUpdatingProperty}
							handleSubmit={(formData) => updateProperty(id, formData)}
							initialBusyProperty={rows.busyProperty}
							isClose={() => handleToggleModal("openUpdateProperty", false)}
						/>

						<ModalUpdateStatus
							open={modalStates.openUpdateStatus}
							isLoading={isLoading}
							isUploading={isUpdatingStatus || isFinalizingStatus}
							handleSubmit={(formData) => {
								const { status } = formData;
								if (status === "finalized") {
									return finalizeStatus(id, formData);
								}
								return updateStatus(id, formData);
							}}
							initialStatus={rows.status}
							isClose={() => handleToggleModal("openUpdateStatus", false)}
						/>

						{modalStates.openFollowUp && (
							<GenericFollowUpTabsModalContent
								open={modalStates.openFollowUp}
								onClose={() => handleToggleModal("openFollowUp", false)}
								clientId={id}
								loggedUser={loggedUser!}
								externalServices={followUpServices}
								internalServices={internalFollowUpServices}
								permissionSendExternal={handlePermissionSendFollowUp(rows.status)}
								// Follow up interno é só-visualização pro sinistro/jurídico — quem escreve é a
								// cobrança (CreditRecoveryFollowUpButton e os modais de lançamento do módulo).
								permissionSendInternal={false}
								permissionAddContact={true}
							/>
						)}

						{modalStates.openResponsible && (
							<ModalUpdateResponsible
								open={modalStates.openResponsible}
								idSinister={rows.id}
								handleSubmit={(data) => updateResponsible(data)}
								isClose={() => handleToggleModal("openResponsible", false)}
								isLoading={isLoading}
								isUploading={isUpdatingResponsible}
							/>
						)}

						{modalStates.openCancel && (
							<ModalCancelSinister
								open={modalStates.openCancel}
								id={id}
								onClose={() => handleToggleModal("openCancel", false)}
								refetch={fetchSinisterDetails}
							/>
						)}

						{modalStates.openCheckList && (
							<ModalDocumentsSinister
								isOpen={modalStates.openCheckList}
								onClose={() => {handleToggleModal("openCheckList", false);fetchChecklist(id, busyProperty);}}
								id={id}
								busyProperty={busyProperty}
								showButtonAttachment={canOpenDocuments}
							/>
						)}
					</>
				)}
			</Grid>
		</LayoutBaseDePagina>
	);
};
